import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import './WanderApp.css';
import WanderApp from './WanderAppComplete';
import { AuthProvider } from './contexts/AuthContext';
import { setErrorReporter } from '@wander/shared/lib/supabase';

// Initialize Sentry error tracking
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    sendDefaultPii: true,
    tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
    replaysSessionSampleRate: 0, // Don't record normal sessions
    replaysOnErrorSampleRate: 1.0, // Record 100% of sessions with errors
  });
}

// Wire up shared error reporter → Sentry so all caught errors (profile, DB, etc.) are tracked
setErrorReporter((error, context) => {
  Sentry.captureException(error instanceof Error ? error : new Error(String(error?.message || error)), {
    tags: { source: 'shared-lib', context: context?.context },
    extra: context,
  });
});

// Suppress benign AbortError rejections from Supabase GoTrueClient
// These occur during React StrictMode double-mounting when auth operations
// are aborted mid-flight. They are harmless and expected.
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.name === 'AbortError') {
    event.preventDefault();
  }
});

// Auto-recover from stale-asset 404s.
//
// When we deploy a new build, content-hashed chunk filenames change
// (main.<hash>.js, *.chunk.js, main.<hash>.css). Vercel's CDN eventually
// purges the old hashes — so a tab that's been open across a deploy will
// keep referencing the old HTML and try to load chunks that no longer exist.
// The browser surfaces this as a 404 + a "Loading chunk failed" / "Failed to
// load resource" / MIME-type-mismatch error and the app silently breaks.
//
// We detect those signals and force a single reload — that fetches fresh HTML
// with the current asset hashes. We use sessionStorage to suppress reload
// loops if something is genuinely broken (not just stale).
const STALE_ASSET_RELOAD_KEY = '__arrivo_stale_asset_reload_at__';
const STALE_ASSET_COOLDOWN_MS = 30_000;

function reloadIfStaleAsset(reason) {
  try {
    const last = Number(sessionStorage.getItem(STALE_ASSET_RELOAD_KEY) || 0);
    if (Date.now() - last < STALE_ASSET_COOLDOWN_MS) return; // already reloaded recently — give up
    sessionStorage.setItem(STALE_ASSET_RELOAD_KEY, String(Date.now()));
  } catch (_) { /* sessionStorage may be unavailable */ }
  // eslint-disable-next-line no-console
  console.warn('[arrivo] Detected stale-asset 404 — reloading to fetch latest build.', reason);
  window.location.reload();
}

// 1. Webpack chunk load failures (dynamic import / lazy chunks)
window.addEventListener('error', (event) => {
  const msg = event?.message || '';
  if (
    /Loading chunk \d+ failed/i.test(msg) ||
    /Loading CSS chunk \d+ failed/i.test(msg) ||
    msg.includes('ChunkLoadError')
  ) {
    reloadIfStaleAsset({ kind: 'chunk-load-error', message: msg });
  }
}, true);

// 2. Failed <script>/<link> resource loads where the URL looks like a
//    hashed asset that no longer exists on the CDN.
window.addEventListener('error', (event) => {
  const target = event?.target;
  if (!target || !(target instanceof HTMLElement)) return;
  const tag = target.tagName;
  if (tag !== 'SCRIPT' && tag !== 'LINK') return;
  const url = target.src || target.href || '';
  if (!url) return;
  if (/\/static\/(js|css)\/[a-zA-Z0-9_-]+\.[a-f0-9]{6,}\.(js|css)$/.test(url)) {
    reloadIfStaleAsset({ kind: 'asset-404', url });
  }
}, true);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<div style={{ padding: 40, textAlign: 'center', fontFamily: 'sans-serif' }}><h2>Something went wrong</h2><p>We've been notified and are looking into it.</p><button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 24px', background: '#1d8e85', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Reload</button></div>}>
      <AuthProvider>
        <WanderApp />
      </AuthProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
